import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plugs, Plus, X, PencilSimple, Check } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const HerokuAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [form, setForm] = useState({ label: '', api_key: '' });

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`/api/heroku/accounts`, { withCredentials: true });
      setAccounts(res.data);
    } catch (e) {
      toast.error("Failed to fetch accounts");
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/heroku/accounts`, form, { withCredentials: true });
      toast.success("Account added successfully");
      setShowAdd(false);
      setForm({ label: '', api_key: '' });
      fetchAccounts();
    } catch (e) {
      toast.error("Failed to add account");
    }
  };

  const removeAccount = async (id) => {
    if (!window.confirm("Are you sure you want to remove this account?")) return;
    try {
      await axios.delete(`/api/heroku/accounts/${id}`, { withCredentials: true });
      fetchAccounts();
      toast.success("Account removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove account");
    }
  };

  const testConnection = async (id) => {
    try {
      const res = await axios.post(`/api/heroku/accounts/${id}/test`, {}, { withCredentials: true });
      toast.success(`Connection OK! Linked to: ${res.data.email}`);
    } catch (e) {
      toast.error("Connection Failed: Invalid API Key");
    }
  };

  const saveRename = async (id) => {
    const label = editLabel.trim();
    if (!label) return toast.error("Nickname cannot be empty");
    try {
      await axios.patch(`/api/heroku/accounts/${id}`, { label }, { withCredentials: true });
      setEditingId(null);
      fetchAccounts();
      toast.success("Nickname updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-black text-[#8b60ed]">HEROKU ACCOUNTS</h1>
        <button 
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-[#8b60ed] text-white px-4 py-2 font-bold hover:bg-[#724dd4] transition-colors"
        >
          <Plus size={20} /> Add Account
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-[#8b60ed]">LINK HEROKU ACCOUNT</h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Label / Nickname</label>
                <input required value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#8b60ed]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Heroku API Key</label>
                <input required type="password" value={form.api_key} onChange={(e) => setForm({...form, api_key: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#8b60ed]" />
              </div>
              <button type="submit" className="w-full bg-[#8b60ed] text-white font-bold py-3 mt-4 hover:bg-[#724dd4] transition-colors">
                LINK ACCOUNT
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {accounts.map(acc => (
          <div key={acc.id} className="border border-[#8b60ed]/20 bg-card p-6 relative group flex flex-col gap-4">
            <div className="flex justify-between items-start">
              {editingId === acc.id ? (
                <div className="flex items-center gap-2">
                  <input autoFocus data-testid={`rename-input-${acc.id}`} value={editLabel} onChange={e => setEditLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveRename(acc.id); if (e.key === 'Escape') setEditingId(null); }}
                    className="bg-[#0A0B0E] border border-[#8b60ed]/50 px-2 py-1 text-lg font-bold focus:outline-none focus:border-[#8b60ed] w-40" />
                  <button onClick={() => saveRename(acc.id)} data-testid={`rename-save-${acc.id}`} className="text-[#39FF14] hover:text-white p-1"><Check size={18} /></button>
                  <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-white p-1"><X size={18} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold">{acc.label}</h3>
                  <button onClick={() => { setEditingId(acc.id); setEditLabel(acc.label); }} data-testid={`rename-btn-${acc.id}`} className="text-slate-500 hover:text-[#8b60ed] p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit nickname"><PencilSimple size={16} /></button>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => testConnection(acc.id)} className="text-slate-400 hover:text-[#8b60ed] transition-colors p-1" title="Test Connection"><Plugs size={20} /></button>
                <CopyApiButton provider="heroku" accountId={acc.id} />
                <button onClick={() => removeAccount(acc.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Remove Account"><Trash size={20} /></button>
              </div>
            </div>
            <div className="space-y-2 text-sm font-mono text-slate-400">
              <p>API Key: <span className="text-white">{acc.api_key}</span></p>
            </div>
          </div>
        ))}
        {accounts.length === 0 && (
          <div className="col-span-full p-8 border border-dashed border-white/20 text-center text-slate-500">
            No Heroku accounts linked.
          </div>
        )}
      </div>
    </div>
  );
};

export default HerokuAccounts;